import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiMaximize2, FiMinimize2, FiMonitor } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, IVORY_GRADIENT_TEXT, Page, Kicker, Tag, Button, EmptyState, cardStyle } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/lab.jpg', pos: 'center 30%', height: 640, opacity: 0.4 };

function resolveEmbedSrc(game) {
  const raw = game?.embedUrl || game?.buildPath;
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  // Relative URL: resolved against the frontend origin (where /games/* assets
  // are served by the build) — NOT the backend API URL.
  return raw.startsWith('/') ? raw : `/${raw}`;
}

function shouldUseIframe(game) {
  const t = (game?.gameType || '').toLowerCase();
  return t === 'unity' || t === 'html5' || t === 'iframe';
}

const TYPE_LABEL = { unity: 'Unity', html5: 'HTML5', iframe: 'Web game', external: 'External' };

export default function GamePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const shellRef = useRef(null);
  const iframeRef = useRef(null);

  // Game → platform bridge (postMessage from the iframe):
  //  - GAME_PROGRESS { score, level, completed, data } → saved on the server; the backend
  //    adds only the improvement over the student's best score to the leaderboard points.
  //  - GAME_EXIT → back to the games list.
  const lastSentRef = useRef('');
  useEffect(() => {
    async function saveProgress(payload) {
      if (!id) return;
      const body = {};
      if (Number.isFinite(Number(payload.score))) body.score = Math.round(Number(payload.score));
      if (Number.isFinite(Number(payload.level))) body.level = Math.round(Number(payload.level));
      if (payload.completed) body.completed = true;
      if (payload.data && typeof payload.data === 'object') body.data = payload.data;
      // Skip exact duplicates (games may fire the same event twice, e.g. on re-render)
      const sig = JSON.stringify(body);
      if (sig === lastSentRef.current) return;
      lastSentRef.current = sig;
      try {
        const { data } = await api.post(`/games/${id}/progress`, body);
        if (data?.pointsEarned > 0) {
          toast.success(`+${data.pointsEarned} points for the leaderboard!`, { icon: '⭐' });
        }
      } catch {
        // Keep playing even if the save fails (offline, rate limit…)
      }
    }

    function onMessage(e) {
      const data = e && e.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'GAME_PROGRESS') {
        saveProgress(data);
        return;
      }
      if (data.type === 'GAME_EXIT') {
        // Exit fullscreen first if we're in one
        if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
        if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
          try { document.webkitExitFullscreen(); } catch (_) {}
        }
        navigate('/giochi');
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate, id]);

  // Pull keyboard focus into the iframe so arrow keys go to the game (and do
  // NOT scroll the host page). Called on mount, click, and fullscreen toggle.
  const focusGame = useCallback(() => {
    try { iframeRef.current?.focus(); iframeRef.current?.contentWindow?.focus(); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { data } = await api.get(`/games/${id}`);
        if (!cancelled) setGame(data.game || null);
      } catch (e) {
        const msg = e.response?.data?.error || 'Unable to load the game';
        toast.error(msg);
        if (!cancelled) setGame(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (id) load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // CSS-fullscreen fallback for browsers (mainly iOS Safari) where the
  // Fullscreen API won't work on a <div> wrapping an <iframe>.
  const [cssFullscreen, setCssFullscreen] = useState(false);

  const onFsChange = useCallback(() => {
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    setFullscreen(!!fsEl || cssFullscreen);
  }, [cssFullscreen]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [onFsChange]);

  const toggleFullscreen = useCallback(async () => {
    const el = shellRef.current;
    if (!el) return;

    const inFs = !!(document.fullscreenElement || document.webkitFullscreenElement) || cssFullscreen;

    try {
      if (!inFs) {
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        } else {
          // iOS Safari & other browsers without div fullscreen → CSS fallback
          setCssFullscreen(true);
          setFullscreen(true);
        }
      } else {
        if (cssFullscreen) {
          setCssFullscreen(false);
          setFullscreen(false);
        } else if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
      }
      // Pull keyboard focus back to the iframe after the transition
      setTimeout(() => focusGame(), 120);
    } catch {
      // If the real API threw (Safari sometimes rejects), degrade to CSS fallback
      setCssFullscreen((v) => !v);
      setFullscreen((v) => !v);
      setTimeout(() => focusGame(), 120);
    }
  }, [focusGame, cssFullscreen]);

  const src = useMemo(() => (game ? resolveEmbedSrc(game) : null), [game]);
  const showIframe = game && shouldUseIframe(game) && src;
  const w = game?.config?.width || 960;
  const h = game?.config?.height || 600;

  if (loading) {
    return <Page hero={HERO}><LoadingSpinner message="Loading the game…" /></Page>;
  }

  if (!game) {
    return (
      <Page hero={HERO} maxWidth={640}>
        <EmptyState
          icon="🦉"
          title="Game not found"
          text="This challenge isn't here anymore. Head back to the training ground and pick another one."
          action={<Button variant="outline" to="/giochi"><FiArrowLeft size={16} /> Back to games</Button>}
        />
      </Page>
    );
  }

  const typeLabel = TYPE_LABEL[(game.gameType || '').toLowerCase()] || game.gameType || 'Game';

  return (
    <Page hero={HERO} maxWidth={1280} style={{ paddingTop: 18 }}>
      {/* header bar: back + title + info chips + fullscreen */}
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ ...cardStyle(), display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', padding: '12px 16px', marginBottom: 18 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flex: 1 }}>
          <Button variant="ghost" size="sm" to="/giochi" aria-label="Back" style={{ width: 42, height: 42, padding: 0, borderRadius: '50%', flexShrink: 0 }}>
            <FiArrowLeft size={20} />
          </Button>
          <div style={{ minWidth: 0 }}>
            <Kicker style={{ fontSize: '0.66rem', letterSpacing: '0.24em' }}>Training ground</Kicker>
            <h1 style={{ fontSize: 'clamp(1.1rem, 2.6vw, 1.5rem)', fontWeight: 900, lineHeight: 1.1, margin: '2px 0 0', color: BRAND.ivory, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {game.title}
            </h1>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Tag>{typeLabel}</Tag>
          <Tag tone="muted"><FiMonitor size={11} /> {w}×{h}</Tag>
          <Button variant="gold" size="sm" onClick={toggleFullscreen}>
            {fullscreen ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
            {fullscreen ? 'Exit' : 'Fullscreen'}
          </Button>
        </div>
      </motion.header>

      {/* game board — the shell itself is the glass card so fixed/fullscreen positioning keeps working */}
      <motion.div
        ref={shellRef}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          ...(fullscreen ? { background: '#000', borderRadius: 0, boxShadow: 'none', border: 'none' } : { ...cardStyle(), padding: 10 }),
          maxWidth: fullscreen ? '100%' : 1240,
          width: fullscreen ? '100vw' : 'auto',
          height: fullscreen ? '100vh' : 'auto',
          margin: '0 auto',
          overflow: 'hidden',
          display: fullscreen ? 'flex' : 'block',
          ...(cssFullscreen ? {
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            maxWidth: '100%',
          } : null),
        }}
      >
        <AnimatePresence mode="wait">
          {showIframe ? (
            <motion.div
              key="iframe"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ width: '100%', height: fullscreen ? '100%' : 'auto', display: 'flex' }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '100%',
                  flex: 1,
                  // In windowed mode keep the native 16:9 aspect ratio;
                  // in fullscreen let the iframe fill the entire viewport.
                  aspectRatio: fullscreen ? 'auto' : `${w} / ${h}`,
                  height: fullscreen ? '100%' : 'auto',
                  background: BRAND.navy900,
                  borderRadius: fullscreen ? 0 : 14,
                  overflow: 'hidden',
                  border: fullscreen ? 'none' : `1px solid ${BRAND.lineSoft}`,
                }}
              >
                <iframe
                  ref={iframeRef}
                  title={game.title}
                  src={src}
                  allowFullScreen={game.config?.allowFullscreen !== false}
                  tabIndex={0}
                  onLoad={focusGame}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block',
                  }}
                />
                {cssFullscreen && (
                  <button
                    type="button"
                    onClick={toggleFullscreen}
                    aria-label="Exit fullscreen"
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      zIndex: 10,
                      width: 44,
                      height: 44,
                      borderRadius: 999,
                      background: 'rgba(7,20,45,0.75)',
                      color: BRAND.goldLight,
                      border: `1px solid ${BRAND.gold}`,
                      fontSize: 20,
                      fontWeight: 900,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: BRAND.font,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="placeholder"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              style={{
                minHeight: 360,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px 24px',
                textAlign: 'center',
                borderRadius: 14,
                background: `radial-gradient(ellipse at 50% 0%, rgba(205,155,80,0.16) 0%, ${BRAND.navy800} 60%)`,
                border: `1px solid ${BRAND.lineSoft}`,
              }}
            >
              <motion.img
                src={BRAND.owl}
                alt=""
                aria-hidden
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}
                style={{ width: 'clamp(84px, 18vw, 128px)', filter: 'drop-shadow(0 12px 30px rgba(205,155,80,0.35))' }}
              />
              <Kicker style={{ marginTop: 22 }}>Mission locked</Kicker>
              <h2 style={{ marginTop: 8, fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 900, lineHeight: 1.1, ...IVORY_GRADIENT_TEXT }}>
                Game coming soon!
              </h2>
              <p style={{ marginTop: 14, maxWidth: 440, fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.2rem', color: 'rgba(249,240,231,0.86)', lineHeight: 1.4 }}>
                Dr. Flash is polishing the last details. Come back soon for a new adventure!
              </p>
              <div style={{ marginTop: 22 }}>
                <Button variant="outline" to="/giochi"><FiArrowLeft size={16} /> Back to games</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {showIframe && !fullscreen && (
        <p style={{ textAlign: 'center', marginTop: 14, fontSize: '0.82rem', color: BRAND.faint, fontWeight: 600 }}>
          Tip: click inside the game so the keyboard controls it. Use <b style={{ color: BRAND.goldLight }}>Fullscreen</b> for the best view.
        </p>
      )}
    </Page>
  );
}
